import { Suspense, useState, useEffect } from 'react';
import type { GetStaticProps } from 'next';
import { getAppI18nConfig, initI18n } from 'cfgi18n';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';

import { RegisterForm } from '../../patterns';
import {
    getRegister,
    GetRegisterResponse,
    getSimpleFooter,
    GetSimpleFooterResponse,
} from 'services/idm';
import { apiErrorHandler } from '@login/helpers';

export type staticProps = {
    footerData: GetSimpleFooterResponse;
};

export const getStaticProps = (async (params) => {
    const locale = params?.locale as string;
    const { supportedLocales } = await getAppI18nConfig('login');
    await initI18n(locale, supportedLocales);
    const footerData = (await getSimpleFooter(locale)) || [];
    return { props: { footerData, locale } };
}) satisfies GetStaticProps<staticProps>;

const Register = () => {
    const { t, i18n } = useTranslation();
    const { replace } = useRouter();
    const [registerData, setRegisterData] =
        useState<GetRegisterResponse | null>(null);

    useEffect(() => {
        fetchRegister();
    }, []);

    const fetchRegister = async () => {
        const data = await getRegister(i18n.language).catch((e) => apiErrorHandler(e, i18n.language));
        if (data) {
            setRegisterData(data);

            if (data.action === 'Redirect' && data.redirectUrl) {
                if (data.redirectUrl.startsWith('/connect')) {
					const absoluteUrl = `${window.location.origin}${data.redirectUrl}`;
					window.location.href = absoluteUrl;
				} else {
					replace(data.redirectUrl);
				}
            }
        }
    };

    return (
        <Suspense>
            {registerData && registerData.action === 'Display' && (
                <RegisterForm
						urls={{
							registerUrl: registerData.placeholders?.registerUrl,
							signInUrl: registerData.placeholders?.signInUrl,
							forgottenPasswordUrl:
								registerData.placeholders?.forgottenPasswordUrl,
							privacyPolicyUrl:
								registerData.placeholders?.privacyPolicyUrl,
							supportName: registerData.placeholders?.supportName,
						}}
						marketingMessage={registerData.marketingMessage}
						disclaimerMessage={registerData.disclaimerMessage}
						token={registerData?.xsrfToken}
						minimumAge={registerData.minimumAge}
                />
            )}
        </Suspense>
    );
};

export default Register;
